import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import {
  PaymentsPrice,
  PaymentsSubscriptionCreateAttributes,
  PaymentsSubscriptionUpdateAttributes,
} from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import NoData from "~/components/containers/NoData";
import { PricingCard } from "~/components/pricing/components/PricingCard";
import { usePricingPlans } from "~/components/pricing/hooks/usePricingPlans";
import { Plan, Product, SubscriptionAddon } from "~/components/pricing/types";
import Spinner from "~/components/ui/spinner/Spinner";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useBillingAccess } from "~/contexts/BillingAccessContext";
import { useTeam } from "~/contexts/TeamProvider";
import { canMember, extractAuthorizations } from "~/helpers/authorizations";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatchParsed } from "~/helpers/tryCatch";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import styles from "./SubscribePage.module.scss";

// Module-level constant so passing it to `usePricingPlans` does not allocate
// a new array reference on every render (which would defeat the hook's memo).
const EMPTY_ADDONS: SubscriptionAddon[] = [];

const SubscribePage = () => {
  usePageTitle(PAGE_TITLES.subscribe);
  const { code } = useParams<{ code: string }>();
  const { team, refetchTeam } = useTeam();
  const { subscription } = useBillingAccess();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // The backend may not let the team change its plan (an Autopto trial, a
  // blocked subscription): the link's catalog then answers 403, so check
  // first and send the user back to billing. No subscription yet means the
  // link creates one.
  const { data: canChangePlan, isLoading: isLoadingAccess } = useQuery({
    queryKey: ["planChangeAccess", team?.id],
    queryFn: async () => {
      if (!team?.subscription) return true;

      const subscriptionResponse = await apiClient.billing.subscriptionList({
        headers: { "Authorize-Member-Actions": "update" },
      });

      return canMember(
        extractAuthorizations(subscriptionResponse.data),
        "update",
      );
    },
    enabled: !!team,
    // Paying lifts the limit: never decide on a cached answer.
    gcTime: 0,
  });

  const {
    data: linkData,
    isLoading: isLoadingLink,
    isError: isLinkError,
  } = useQuery({
    queryKey: ["subscriptionLink", code],
    queryFn: () => apiClient.billing.subscriptionLinksDetail(code!),
    enabled: !!code && canChangePlan === true,
  });

  const link = linkData?.data?.["payments/subscription_link"];

  const subscriptionAddons = subscription?.subscription_addons as
    | SubscriptionAddon[]
    | undefined;
  const subsctiptionPriceId = subscription?.price?.id;

  const { mutateAsync: updateAsync } = useMutation({
    mutationFn: (data: PaymentsSubscriptionUpdateAttributes) =>
      apiClient.billing.subscriptionPartialUpdate(data),
  });

  const { mutateAsync: subscribeAsync } = useMutation({
    mutationFn: (data: PaymentsSubscriptionCreateAttributes) =>
      apiClient.billing.subscriptionCreate(data),
  });

  // Build the input for `usePricingPlans` purely from the link response: the
  // link's own product as the single plan (with the targeted price moved to
  // `prices[0]`, since the hook exposes only `prices[0].id` as `priceId`) +
  // a synthetic product per addon, constructed from the embedded
  // `addonPrice.product` with the addonPrice itself as its sole `prices[]`
  // entry so the hook's tier lookup matches.
  const mergedProducts = useMemo(() => {
    const linkProduct = link?.product;
    const linkPriceId = link?.price_id;
    if (!linkProduct || linkPriceId === undefined) return [];

    const targetPrice = linkProduct.prices?.find((p) => p.id === linkPriceId);
    if (!targetPrice) return [];

    const addonProductFromPrice = (addonPrice: PaymentsPrice) =>
      addonPrice.product
        ? ({
            ...addonPrice.product,
            prices: [addonPrice],
          } as unknown as Product)
        : null;

    const addonProducts = [
      ...(linkProduct.optional_addons ?? []),
      ...(linkProduct.default_addons ?? []),
    ]
      .map(addonProductFromPrice)
      .filter((p): p is Product => p !== null);

    const injectedPlan = {
      ...linkProduct,
      prices: [
        targetPrice,
        ...(linkProduct.prices ?? []).filter((p) => p.id !== targetPrice.id),
      ],
    } as unknown as Product;

    return [...addonProducts, injectedPlan];
  }, [link?.product, link?.price_id]);

  const plan = usePricingPlans(
    mergedProducts,
    subscriptionAddons ?? EMPTY_ADDONS,
  )?.[0];

  const onSubscribe = async (
    data: Omit<PaymentsSubscriptionCreateAttributes, "ua">,
  ) => {
    if (!code) return;
    const isUpdate = !!team?.subscription;
    const verb = isUpdate ? "updating" : "creating";

    const result = isUpdate
      ? await tryCatchParsed(updateAsync({ ...data, subscription_code: code }))
      : await tryCatchParsed(
          subscribeAsync({ ...data, ua: "undefined", subscription_code: code }),
        );

    if (result.error) {
      enqueueSnackbar(
        `Error ${verb} subscription: ${messageFromError(result.error)}`,
        { variant: "error", persist: true },
      );

      return;
    }

    enqueueSnackbar(
      `Subscription was successfully ${isUpdate ? "updated" : "created"}!`,
      { variant: "success" },
    );
    // The link may be single-use on the backend; drop its cache so a stale
    // copy cannot satisfy a second attempt against the same code.
    queryClient.removeQueries({ queryKey: ["subscriptionLink", code] });
    await Promise.all([
      refetchTeam(),
      queryClient.invalidateQueries({ queryKey: ["subscription"] }),
    ]);
    navigate(routes.billing.subscription(), {
      state: { openCheckout: true },
    });
  };

  // Use isLoading (initial fetch only), not isFetching, so background refetches
  // do not blank the whole page. Mutation pending state is handled separately
  // below so the PricingCard does not unmount mid-submit and lose slider state.
  if (!team || isLoadingAccess || isLoadingLink) {
    return <Spinner />;
  }

  if (canChangePlan === false) {
    return <Navigate to={routes.billing.subscription()} replace />;
  }

  if (!code || isLinkError || !link || !plan) {
    return (
      <NoData
        title="Subscription link unavailable"
        path={routes.billing.subscriptionEdit()}
        buttonLabel="Browse plans"
      />
    );
  }

  return (
    <div className={styles.layout}>
      <h1 className={styles.title}>Subscribe to {plan.name}</h1>
      {(link.trial_days ?? 0) > 0 && (
        <div className={styles.trialBanner}>
          {link.trial_days}-day free trial included
        </div>
      )}
      <div className={styles.cardWrapper}>
        <PricingCard
          plan={plan as unknown as Plan}
          mutate={onSubscribe}
          subsctiptionPriceId={subsctiptionPriceId}
          personalized
        />
      </div>
    </div>
  );
};

export default SubscribePage;
